/**
 * GridTable Component
 * 
 * Data table using DIVs and CSS Grid for perfect column alignment.
 * All cells (header + body) are direct children of the grid - no row wrappers needed.
 * 
 * @package PresszoneComments
 */

import { el, __ } from '../utils/dom.js';
import { EmptyState } from './EmptyState.js';

export default class GridTable {
    constructor(options = {}) {
        this.options = {
            columns: [], // [{ label: 'Name', flex: 2, minWidth: '180px' }, { label: 'Action', width: '100px' }]
            className: 'presszone-comments-grid-table',
            emptyMessage: __('No items found.', 'comments-press-zone'),
            emptyIcon: '📋',
            emptyTitle: null, // If null, will derive from emptyMessage
            emptyDescription: null,
            ...options
        };
    }

    /**
     * Generate grid-template-columns value from column definitions.
     * Fixed width columns use their width, flexible columns use minmax().
     */
    getGridTemplateColumns() {
        return this.options.columns.map(col => {
            if (col.width) {
                // Fixed width column
                return col.width;
            }
            // Flexible column with minmax
            const minWidth = col.minWidth || '80px';
            const flex = col.flex || 1;
            return `minmax(${minWidth}, ${flex}fr)`;
        }).join(' ');
    }

    /**
     * Render the table with data.
     * @param {Array|null} data - Data array or null for loading state
     * @param {Function} rowRenderer - Function that returns an array of cell contents (not wrapped in row)
     */
    render(data, rowRenderer) {
        const wrapper = el('div', { class: 'presszone-comments-table-wrapper' });

        if (!data) {
            wrapper.appendChild(this.renderLoading());
            return wrapper;
        }

        if (data.length === 0) {
            wrapper.appendChild(this.renderEmpty());
            return wrapper;
        }

        const columnCount = this.options.columns.length;
        const gridTemplateColumns = this.getGridTemplateColumns();

        // Create the grid container
        const grid = el('div', {
            class: this.options.className,
            role: 'table'
        });

        // Apply grid-template-columns via style property (object form works with el())
        grid.style.gridTemplateColumns = gridTemplateColumns;

        // Header cells (direct children of grid)
        this.options.columns.forEach((col, index) => {
            const headerCell = el('div', {
                class: 'presszone-comments-grid-table__cell presszone-comments-grid-table__cell--header',
                role: 'columnheader'
            }, col.label);
            grid.appendChild(headerCell);
        });

        // Body cells (direct children of grid, auto-wrapped by CSS Grid)
        data.forEach((item, rowIndex) => {
            // rowRenderer returns array of cell CONTENTS (not wrapped cells)
            const cellContents = rowRenderer(item);
            
            cellContents.forEach((content, colIndex) => {
                const cell = el('div', {
                    class: 'presszone-comments-grid-table__cell',
                    role: 'cell'
                }, content);
                
                // Add row-start class for first cell in each row (for row styling)
                if (colIndex === 0) {
                    cell.classList.add('presszone-comments-grid-table__cell--row-start');
                }
                
                // Add data attribute for row index (useful for hover effects via CSS)
                cell.dataset.row = rowIndex;
                
                grid.appendChild(cell);
            });
        });

        wrapper.appendChild(grid);
        
        // Add scroll detection for mobile
        this.addScrollDetection(wrapper);
        
        return wrapper;
    }

    addScrollDetection(wrapper) {
        const checkScrollable = () => {
            if (window.innerWidth <= 768) {
                const isScrollable = wrapper.scrollWidth > wrapper.clientWidth;
                wrapper.setAttribute('data-scrollable', isScrollable.toString());
            } else {
                wrapper.removeAttribute('data-scrollable');
            }
        };

        setTimeout(checkScrollable, 100);
        window.addEventListener('resize', checkScrollable);
        
        // Clean up listener when element is removed
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.removedNodes.forEach((node) => {
                        if (node === wrapper) {
                            window.removeEventListener('resize', checkScrollable);
                            observer.disconnect();
                        }
                    });
                }
            });
        });
        
        if (wrapper.parentNode) {
            observer.observe(wrapper.parentNode, { childList: true });
        }
    }

    renderEmpty(message = null) {
        const icon = this.options.emptyIcon;
        const title = this.options.emptyTitle || message || this.options.emptyMessage;
        const description = this.options.emptyDescription || '';
        
        return EmptyState(icon, title, description);
    }

    renderLoading(message = __('Loading...', 'comments-press-zone')) {
        return el('div', { class: 'presszone-comments-loading' },
            el('div', { class: 'presszone-comments-spinner' }),
            el('p', {}, message)
        );
    }
}
